import ReactGA4 from "react-ga4";
import TagManager from "react-gtm-module";
import type { createBrowserRouter } from "react-router-dom";
import { matchRoutes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import axios from "axios";
import Cookies from "js-cookie";
import { z } from "zod";

import { ACCESS_DENIED, NOT_FOUND_PAGE_ID } from "./apps/common/constants";
import { buildFiltersURL, paramsToObject } from "./apps/common/filters";
import {
  requestFinished,
  requestStarted,
} from "./components/request-loader/logic";
import { deleteAmplitudeCookies } from "./services/analytics/utils";
import { isErrorUnwantedToLog, processEvent } from "./services/sentry";
import { globalConfig, UNEXPECTED_NETWORK_ERROR_MESSAGE } from "./constants";
import { getCurrentPathEncoded, getURL } from "./utils";

/** https://github.com/colinhacks/zod/blob/master/ERROR_HANDLING.md#customizing-errors-with-zoderrormap */
const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (
    issue.code === z.ZodIssueCode.invalid_date &&
    ctx.defaultError === "Invalid date"
  ) {
    return { message: "Please enter a valid date" };
  }
  return { message: ctx.defaultError };
};

z.setErrorMap(customErrorMap);

const SENTRY_ERROR_RATE_LIMIT = 50;
let SentryErrorCount = 0;
let SentryErrorInterval: number | undefined;

export const configureServices = () => {
  if (SETTINGS.SENTRY_ENABLED) {
    Sentry.init({
      dsn: SETTINGS.SENTRY_DSN,
      integrations: [
        Sentry.dedupeIntegration(),
        Sentry.extraErrorDataIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          block: ["iframe"], // prevents replay from trying to record iframes
        }),
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
      normalizeDepth: 5,
      beforeSend(event, hint) {
        // additional condition to filter unwanted errors that cannot be handled by 'ignoreErrors' and 'denyUrls'
        if (isErrorUnwantedToLog(event, hint)) {
          return null;
        }

        SentryErrorCount++;

        // Do not send errors to the sentry if the limit exceeded.
        if (SENTRY_ERROR_RATE_LIMIT <= SentryErrorCount) {
          if (!SentryErrorInterval) {
            SentryErrorInterval = window.setInterval(
              () => {
                clearInterval(SentryErrorInterval);
                SentryErrorCount = 0;
                SentryErrorInterval = undefined;
              },
              1000 * 60 * 60,
            ); // 1h
          }

          return null;
        }

        const processedEvent = processEvent(event);

        return processedEvent;
      },
      // https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
      ignoreErrors: [
        // Random plugins/extensions
        "top.GLOBALS",
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.epicplay.com",
        "Can't find variable: ZiteReader",
        "jigsaw is not defined",
        "ComboSearch is not defined",
        "http://loading.retry.widdit.com/",
        "atomicFindClose",
        // Facebook borked
        "fb_xd_fragment",
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        "bmi_SafeAddOnload",
        "EBCallBackMessageReceived",
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        "conduitPage",
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        // ---
        // Our custom list below
        "Script error.",
        "Mixpanel error",
        "instantSearchSDKJSBridgeClearHighlight",
        "zaloJSV2",
        "_avast_submit",
        "MetaMask - RPC Error: Request of type 'wallet_requestPermissions' already pending for origin",
        /MetaMask: Received invalid \w* parameter/i,
        "chrome-extension://",
        "moz-extension://",
        "safari-extension://",
        "webkit-masked-url://hidden/",
        "Network Error",
        "window.webkit.messageHandlers",
        "Unsupported chain id:",
        "UCShellJava.sdkEventFire",
        "Failed to execute 'removeChild' on 'Node'",
        "translate.goog",
        "ResizeObserver loop limit exceeded",
        "beacon",
        "gtag is not defined",
        "fbq is not defined",
        "google is not defined",
        "Can't find variable: gtag",
        "Can't find variable: fbq",
        "Can't find variable: google",
        "ChunkLoadError",
        /widget\/freddyfeedback/i,
        // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-1233146122
        /Object Not Found Matching Id/i,
        /Error: Failed to load Stripe\.js/i,
        /Timeout/i,
      ],
      denyUrls: [
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        /js\-agent\.newrelic\.com/i,
        /freddyfeedback\.com\/api\/v1\/survey\/.*/i,
        /gstatic\.com\/recaptcha/i,
        /googleads\.g\.doubleclick/,
        /googletagmanager\.com/i,
        /web-sdk\.smartlook\.com/i,
        /analytics\.google\.com/i,
      ],
    });
  }

  if (SETTINGS.GOOGLE_ANALYTICS_V4_ID) {
    ReactGA4.initialize(SETTINGS.GOOGLE_ANALYTICS_V4_ID);
  }

  if (SETTINGS.GOOGLE_TAG_MANAGER_ID) {
    TagManager.initialize({
      gtmId: SETTINGS.GOOGLE_TAG_MANAGER_ID,
    });
  }
};

// Related to firefox issue, when during fetching user reloads page, multiple snackbars are shown
window.addEventListener("beforeunload", () => {
  globalConfig.surpressDisplaySnackbarOnBeforeUnload = true;
});

export const configureRequestsLibrary = (
  router: ReturnType<typeof createBrowserRouter>,
) => {
  axios.interceptors.request.use((config) => {
    requestStarted();
    config.headers.set("X-CSRFToken", Cookies.get("csrftoken"));

    return config;
  });

  axios.defaults.headers.common["Backend-Revision"] = SETTINGS.BACKEND_REVISION;

  axios.interceptors.response.use(
    (response) => {
      requestFinished();
      return response;
    },
    (errorResponse) => {
      requestFinished();
      const { response } = errorResponse;
      const nonEmptyResponse = response || { data: {}, status: null };
      const { status, config } = nonEmptyResponse;

      switch (status) {
        case 404: {
          if (config.method !== "get") {
            break;
          }

          const matches = matchRoutes(
            [
              {
                notFoundId: NOT_FOUND_PAGE_ID.FREELANCER_PROFILE,
                path: "/talent/:id",
              },
              {
                notFoundId: NOT_FOUND_PAGE_ID.EMPLOYER_PROFILE,
                path: "/employers/:id",
              },
              {
                notFoundId: NOT_FOUND_PAGE_ID.TALENT_OFFER_PREVIEW,
                path: "/jobs/:jobId/offers/:id",
              },
              {
                notFoundId: ENUMS.JobNotFoundErrorCode.JOB_NOT_FOUND,
                path: "/jobs/:id",
              },
            ],
            window.location?.pathname,
          );

          const matchWithError = matches?.find(
            (match) => match.route.notFoundId,
          );

          const Http404Code = matchWithError
            ? matchWithError.route.notFoundId
            : nonEmptyResponse.data.detail;

          if (Http404Code === "Invalid page.") {
            const filters = paramsToObject(window.location.search);
            const newUrl = buildFiltersURL(filters);
            newUrl.setQuery("page", 1);
            const to = getURL(newUrl.toString());
            void router.navigate(to, { replace: true });
            return;
          }

          if (Http404Code) {
            const url = `/page-not-found/?message=${Http404Code}&origin=${window.location.href}`;
            void router.navigate(url, {
              state: nonEmptyResponse.data,
              replace: true,
            });
          }

          break;
        }
        case 403: {
          const matches = matchRoutes(
            [
              {
                notFoundId: ACCESS_DENIED.JOB_ACCESS_DENIED,
                path: "/jobs/:id",
              },
            ],
            window.location?.pathname,
          );

          const matchWithError = matches?.find(
            (match) => match.route.notFoundId,
          );

          const errorDetail = nonEmptyResponse?.data?.detail;
          if (
            typeof errorDetail === "string" &&
            errorDetail.startsWith("CSRF Failed")
          ) {
            void router.navigate("/auth/logout/", { state: { prevPath: "/" } });
            break;
          }

          const Http403Code = matchWithError
            ? matchWithError.route.notFoundId
            : nonEmptyResponse.data.detail;

          if (Http403Code) {
            const url = `/access-denied/?message=${Http403Code}`;
            void router.navigate(url, {
              state: nonEmptyResponse.data,
              replace: true,
            });
          }
          break;
        }
        case 401:
          if (window.currentRouteRequireAuth) {
            deleteAmplitudeCookies();
            window.location.href = `/auth/login/?message=session_expired&next=${getCurrentPathEncoded()}`;
          }
          break;
        case 429:
          void router.navigate(`/429/`);
          break;
        default:
          if (
            axios.isCancel(errorResponse) ||
            errorResponse?.code === "ECONNABORTED" // it seems that cancelation without a token is not passing axios.isCancel(errorResponse) - cancelling request by browser when you change page
          ) {
            // Cancelled on purpose, return empty error
          } else if (!errorResponse.response) {
            // The request was made but no response was received.
            nonEmptyResponse.data = {
              _error: UNEXPECTED_NETWORK_ERROR_MESSAGE,
            };
          }

          errorResponse.response = {
            ...errorResponse.response,
            ...nonEmptyResponse,
          };
      }

      if (globalConfig.surpressDisplayGenericRequestError) {
        return {};
      } else {
        return Promise.reject(errorResponse);
      }
    },
  );
};
